// Phase HR-002 — Mobile-first My To-Do redesign.
//
// Operator persona: every employee opens this daily, often on phone.
//
// What changed vs the prior table-based view:
//   * Card-based vertical list — single column on mobile, 2-up on tablet,
//     3-up on desktop. No horizontal scroll.
//   * Priority sections (P1, P2, P3, OTHER) so overdue/critical work
//     surfaces at top of screen.
//   * Status filter chips: All / Pending / In Progress / Overdue /
//     Completed / Verified / Blocked. One tap to filter.
//   * Yesterday / Today / Tomorrow date chips + a date picker for arbitrary
//     dates. No more 3-tap calendar widget for the common case.
//   * Big primary "Complete" button per card. Camera/file upload sits
//     right in the complete sheet — no third tap to find an upload field.
//   * Live Study tasks still jump to the live-capture page (the Phase 0090
//     bridge) on the green "Start Live Study" button.
//   * Sticky bottom action bar with refresh + new (manual task creation
//     deferred — relies on the generate cron).
//   * Reduced-motion respected via shared hrMobilePolish styles.

import { useMemo, useState } from 'react'
import {
  Alert, App, Avatar, Button, Card, Drawer, Empty, Form, Input, Modal,
  Progress, Select, Skeleton, Space, Tag, Tooltip, Typography, Upload,
  type UploadFile,
} from 'antd'
import {
  CameraOutlined, CheckCircleFilled, CheckCircleOutlined, ClockCircleOutlined,
  ExclamationCircleFilled, FileImageOutlined, PaperClipOutlined,
  PauseCircleOutlined, PlayCircleOutlined, ReloadOutlined, RocketOutlined,
  StopOutlined, WarningOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'

import { taskApi, type TaskInstance, type TaskStatus } from '@/features/hr/taskExecutionApi'
import RaiseGrievanceModal from './RaiseGrievanceModal'
import PageHeader from '@/shared/components/PageHeader'
import {
  useHrMobilePolish, attendancePctClass, employeeInitials,
} from '@/shared/styles/hrMobilePolish'

const { Text, Title } = Typography

const STATUS_LABEL: Record<TaskStatus, string> = {
  PENDING:     'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED:   'Done',
  VERIFIED:    'Verified',
  REJECTED:    'Rejected',
  OVERDUE:     'Overdue',
  BLOCKED:     'Blocked',
  SKIPPED:     'Skipped',
  CANCELLED:   'Cancelled',
}
const STATUS_COLOR: Record<TaskStatus, string> = {
  PENDING:     'default',
  IN_PROGRESS: 'processing',
  COMPLETED:   'cyan',
  VERIFIED:    'green',
  REJECTED:    'red',
  OVERDUE:     'volcano',
  BLOCKED:     'orange',
  SKIPPED:     'default',
  CANCELLED:   'default',
}
const PRIORITY_COLOR: Record<string, string> = {
  P1: '#cf1322',
  P2: '#fa8c16',
  P3: '#1677ff',
}
const PRIORITY_ORDER = ['P1', 'P2', 'P3', 'OTHER'] as const
type PrioKey = typeof PRIORITY_ORDER[number]

type StatusFilter = 'ALL' | 'OPEN' | TaskStatus
const OPEN_STATUSES = new Set<TaskStatus>(
  ['PENDING', 'IN_PROGRESS', 'OVERDUE', 'BLOCKED'],
)

export default function MyTodoPage() {
  useHrMobilePolish()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [onDate, setOnDate] = useState<Dayjs>(dayjs())
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN')
  const [completeTask, setCompleteTask] = useState<TaskInstance | null>(null)
  const [blockTask, setBlockTask] = useState<TaskInstance | null>(null)
  const [recentSavedId, setRecentSavedId] = useState<string | null>(null)
  // Phase HR-003 Wave 3 — Raise grievance from My-To-Do header
  const [raiseGrievOpen, setRaiseGrievOpen] = useState(false)

  const dateStr = onDate.format('YYYY-MM-DD')
  const { data: tasks = [], isLoading, refetch } = useQuery({
    queryKey: ['my-todo', dateStr],
    queryFn: () => taskApi.myTasks(dateStr),
    refetchOnWindowFocus: true,
  })

  // ─── Counters + threshold tile for "Done %" ────────────────────────
  const stats = useMemo(() => {
    const total = tasks.length
    let pending = 0, inProgress = 0, completed = 0, verified = 0,
        overdue = 0, blocked = 0, rejected = 0
    for (const t of tasks) {
      switch (t.status) {
        case 'PENDING':     pending++; break
        case 'IN_PROGRESS': inProgress++; break
        case 'COMPLETED':   completed++; break
        case 'VERIFIED':    verified++; break
        case 'OVERDUE':     overdue++; break
        case 'BLOCKED':     blocked++; break
        case 'REJECTED':    rejected++; break
      }
    }
    const closed = completed + verified
    const open = pending + inProgress + overdue + blocked
    const donePct = total ? Math.round((closed / total) * 100) : null
    return { total, pending, inProgress, completed, verified,
             overdue, blocked, rejected, closed, open, donePct }
  }, [tasks])

  // ─── Apply filters + group by priority ─────────────────────────────
  const filtered = useMemo(() => {
    if (statusFilter === 'ALL') return tasks
    if (statusFilter === 'OPEN') return tasks.filter(t => OPEN_STATUSES.has(t.status))
    return tasks.filter(t => t.status === statusFilter)
  }, [tasks, statusFilter])

  const grouped = useMemo(() => {
    const buckets: Record<PrioKey, TaskInstance[]> = {
      P1: [], P2: [], P3: [], OTHER: [],
    }
    for (const t of filtered) {
      const k = (t.priority && PRIORITY_ORDER.includes(t.priority as PrioKey))
        ? (t.priority as PrioKey) : 'OTHER'
      buckets[k].push(t)
    }
    // Within each bucket, overdue > in-progress > pending > others;
    // then by due time ascending.
    const order: Record<TaskStatus, number> = {
      OVERDUE: 0, IN_PROGRESS: 1, PENDING: 2, BLOCKED: 3,
      COMPLETED: 4, VERIFIED: 5, REJECTED: 6, SKIPPED: 7, CANCELLED: 8,
    }
    for (const k of PRIORITY_ORDER) {
      buckets[k].sort((a, b) => {
        const so = order[a.status] - order[b.status]
        if (so !== 0) return so
        const da = a.due_at ? new Date(a.due_at).getTime() : Infinity
        const db = b.due_at ? new Date(b.due_at).getTime() : Infinity
        return da - db
      })
    }
    return buckets
  }, [filtered])

  // ─── Mutations ─────────────────────────────────────────────────────
  function flashSaved(id: string) {
    setRecentSavedId(id)
    setTimeout(() => setRecentSavedId(prev => prev === id ? null : prev), 800)
  }
  const startMut = useMutation({
    mutationFn: (t: TaskInstance) => taskApi.start(t.id),
    onSuccess: (_data, t) => {
      message.success('Started')
      flashSaved(t.id)
      qc.invalidateQueries({ queryKey: ['my-todo', dateStr] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="My To-Do"
        subtitle="Your tasks today. Tap Complete when done; the system tracks the rest."
        extra={
          // Phase HR-003 Wave 3 — every worker can raise a grievance from
          // here. Anonymous mode is a toggle inside the modal.
          <Button onClick={() => setRaiseGrievOpen(true)}>
            Raise grievance
          </Button>
        }
      />

      <RaiseGrievanceModal
        open={raiseGrievOpen}
        onClose={() => setRaiseGrievOpen(false)}
        onRaised={() => { /* no list refresh needed here */ }}
      />

      {/* ── Date chip row ───────────────────────────────────────────── */}
      <Space wrap style={{ marginBottom: 12 }}>
        <DateChipRow value={onDate} onChange={setOnDate} />
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
      </Space>

      {/* ── KPI tiles ───────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 8, marginBottom: 12,
      }}>
        <TodoTile label="Open" value={stats.open}
                  tone={stats.overdue > 0 ? 'red' : stats.open > 0 ? 'amber' : 'green'}
                  subtitle={`${stats.overdue} overdue`} />
        <TodoTile label="In Progress" value={stats.inProgress} tone="neutral" />
        <TodoTile label="Done" value={stats.closed}
                  tone={stats.donePct != null && stats.total > 0
                          ? attendancePctClass(stats.donePct)
                          : 'neutral'}
                  subtitle={stats.donePct != null ? `${stats.donePct}%` : undefined} />
        <TodoTile label="Blocked" value={stats.blocked}
                  tone={stats.blocked > 0 ? 'amber' : 'neutral'} />
      </div>

      {/* ── Status filter chips ─────────────────────────────────────── */}
      <div className="hr-filter-chips" style={{ marginBottom: 16 }}>
        {[
          { v: 'OPEN'    as const, label: `Open (${stats.open})` },
          { v: 'OVERDUE' as const, label: `Overdue (${stats.overdue})` },
          { v: 'IN_PROGRESS' as const, label: `In Progress (${stats.inProgress})` },
          { v: 'PENDING' as const, label: `Pending (${stats.pending})` },
          { v: 'BLOCKED' as const, label: `Blocked (${stats.blocked})` },
          { v: 'COMPLETED' as const, label: `Done (${stats.completed})` },
          { v: 'VERIFIED' as const, label: `Verified (${stats.verified})` },
          { v: 'ALL'     as const, label: `All (${stats.total})` },
        ].map(({ v, label }) => (
          <button
            key={v}
            type="button"
            className={'hr-filter-chip' + (statusFilter === v ? ' hr-filter-chip--active' : '')}
            onClick={() => setStatusFilter(v)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Grouped task list ───────────────────────────────────────── */}
      {isLoading ? (
        <Skeleton active />
      ) : stats.total === 0 ? (
        <Empty description={
          <>
            <div>No tasks for this date.</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Either you're caught up, or generation hasn't run yet.
            </Text>
          </>
        } />
      ) : filtered.length === 0 ? (
        <Empty description={`No tasks match this filter.`} />
      ) : (
        <div style={{ display: 'grid', gap: 18 }}>
          {PRIORITY_ORDER.map(prio => grouped[prio].length > 0 && (
            <PriorityGroup
              key={prio}
              priority={prio}
              tasks={grouped[prio]}
              recentSavedId={recentSavedId}
              startBusy={(id) => startMut.isPending && startMut.variables?.id === id}
              onStartLiveStudy={(t) =>
                navigate(`/hr/live-studies/start?study_id=${t.study_id}&task_id=${t.id}`)
              }
              onStart={(t) => startMut.mutate(t)}
              onComplete={(t) => setCompleteTask(t)}
              onBlock={(t) => setBlockTask(t)}
            />
          ))}
        </div>
      )}

      {/* ── Complete sheet (camera/file evidence supported) ──────────── */}
      <CompleteSheet
        task={completeTask}
        onClose={() => setCompleteTask(null)}
        onCompleted={(t) => {
          message.success('Marked complete — awaiting verification.')
          flashSaved(t.id)
          qc.invalidateQueries({ queryKey: ['my-todo', dateStr] })
        }}
      />

      {/* ── Block sheet ─────────────────────────────────────────────── */}
      <BlockSheet
        task={blockTask}
        onClose={() => setBlockTask(null)}
        onBlocked={(t) => {
          message.success('Marked blocked.')
          flashSaved(t.id)
          qc.invalidateQueries({ queryKey: ['my-todo', dateStr] })
        }}
      />
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────

function DateChipRow({ value, onChange }: {
  value: Dayjs
  onChange: (d: Dayjs) => void
}) {
  const today = dayjs().startOf('day')
  const isToday     = value.isSame(today, 'day')
  const isYesterday = value.isSame(today.subtract(1, 'day'), 'day')
  const isTomorrow  = value.isSame(today.add(1, 'day'), 'day')
  return (
    <Space size={4} wrap>
      <button type="button"
        className={'hr-filter-chip' + (isYesterday ? ' hr-filter-chip--active' : '')}
        onClick={() => onChange(today.subtract(1, 'day'))}>Yesterday</button>
      <button type="button"
        className={'hr-filter-chip' + (isToday ? ' hr-filter-chip--active' : '')}
        onClick={() => onChange(today)}>Today</button>
      <button type="button"
        className={'hr-filter-chip' + (isTomorrow ? ' hr-filter-chip--active' : '')}
        onClick={() => onChange(today.add(1, 'day'))}>Tomorrow</button>
    </Space>
  )
}

function TodoTile({ label, value, tone, subtitle }: {
  label: string
  value: number
  tone: 'green' | 'amber' | 'red' | 'neutral'
  subtitle?: string
}) {
  return (
    <div className={`threshold-tile threshold-tile--${tone}`}>
      <div className="threshold-tile__label">{label}</div>
      <div className="threshold-tile__value">{value}</div>
      {subtitle && (
        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
          {subtitle}
        </div>
      )}
    </div>
  )
}

function PriorityGroup({
  priority, tasks, recentSavedId, startBusy,
  onStartLiveStudy, onStart, onComplete, onBlock,
}: {
  priority: PrioKey
  tasks: TaskInstance[]
  recentSavedId: string | null
  startBusy: (id: string) => boolean
  onStartLiveStudy: (t: TaskInstance) => void
  onStart: (t: TaskInstance) => void
  onComplete: (t: TaskInstance) => void
  onBlock: (t: TaskInstance) => void
}) {
  const priColor = PRIORITY_COLOR[priority] ?? '#8c8c8c'
  const priLabel = priority === 'OTHER' ? 'Other' : priority
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginBottom: 8, padding: '4px 0',
      }}>
        <div style={{
          width: 4, height: 18, borderRadius: 2,
          background: priColor,
        }} />
        <Text strong style={{ fontSize: 13, color: priColor, letterSpacing: 0.5 }}>
          {priLabel}
        </Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {tasks.length} task{tasks.length === 1 ? '' : 's'}
        </Text>
      </div>
      <div style={{
        display: 'grid', gap: 10,
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      }}>
        {tasks.map(t => (
          <TodoCard
            key={t.id}
            task={t}
            recentSaved={recentSavedId === t.id}
            startBusy={startBusy(t.id)}
            onStartLiveStudy={onStartLiveStudy}
            onStart={onStart}
            onComplete={onComplete}
            onBlock={onBlock}
          />
        ))}
      </div>
    </div>
  )
}

function TodoCard({
  task, recentSaved, startBusy,
  onStartLiveStudy, onStart, onComplete, onBlock,
}: {
  task: TaskInstance
  recentSaved: boolean
  startBusy: boolean
  onStartLiveStudy: (t: TaskInstance) => void
  onStart: (t: TaskInstance) => void
  onComplete: (t: TaskInstance) => void
  onBlock: (t: TaskInstance) => void
}) {
  const isOpen = OPEN_STATUSES.has(task.status)
  const isOverdue = task.status === 'OVERDUE'
  const isStudy = !!task.study_id
  const dueLabel = task.due_at ? dayjs(task.due_at).format('HH:mm') : '—'
  return (
    <Card
      size="small"
      bordered={false}
      className={recentSaved ? 'hr-save-success' : undefined}
      style={{
        borderRadius: 12,
        borderLeft: `4px solid ${
          isOverdue ? '#cf1322'
          : task.priority === 'P1' ? '#cf1322'
          : task.priority === 'P2' ? '#fa8c16'
          : '#bfbfbf'
        }`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
      styles={{ body: { padding: '14px 16px' } }}
    >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {/* Title + status badge row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: 15, lineHeight: 1.3,
                                   display: 'block' }}>
              {task.title}
            </Text>
            {task.description && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block',
                                              marginTop: 2 }}>
                {task.description.length > 90
                  ? task.description.slice(0, 90) + '…'
                  : task.description}
              </Text>
            )}
          </div>
          <Tag color={STATUS_COLOR[task.status]} style={{ marginInlineEnd: 0 }}>
            {STATUS_LABEL[task.status]}
          </Tag>
        </div>
        {/* Meta row: due + escalation */}
        <Space size={6} wrap>
          <Tag icon={<ClockCircleOutlined />} color="default">Due {dueLabel}</Tag>
          {task.escalation_level > 0 && (
            <Tag color="red" icon={<WarningOutlined />}>
              Escalated L{task.escalation_level}
            </Tag>
          )}
          {task.evidence_required && !task.evidence_url && (
            <Tag color="orange" icon={<FileImageOutlined />}>
              Evidence required
            </Tag>
          )}
          {task.evidence_url && (
            <Tag color="green" icon={<PaperClipOutlined />}>
              Evidence attached
            </Tag>
          )}
        </Space>
        {/* Actions */}
        {isOpen && (
          <Space size={8} wrap>
            {isStudy ? (
              <Button
                type="primary"
                icon={<RocketOutlined />}
                onClick={() => onStartLiveStudy(task)}
                style={{ background: '#52c41a', borderColor: '#52c41a',
                          height: 40, fontWeight: 600, borderRadius: 8 }}
              >
                Start Live Study
              </Button>
            ) : (
              <>
                {task.status === 'PENDING' && (
                  <Button
                    icon={<PlayCircleOutlined />}
                    onClick={() => onStart(task)}
                    loading={startBusy}
                    style={{ height: 40, borderRadius: 8 }}
                  >
                    Start
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => onComplete(task)}
                  style={{ height: 40, fontWeight: 600, borderRadius: 8 }}
                >
                  Complete
                </Button>
                <Button
                  danger
                  icon={<StopOutlined />}
                  onClick={() => onBlock(task)}
                  style={{ height: 40, borderRadius: 8 }}
                >
                  Block
                </Button>
              </>
            )}
          </Space>
        )}
        {/* Closed states */}
        {task.status === 'COMPLETED' && (
          <Tag icon={<CheckCircleFilled />} color="cyan">
            Awaiting verification
          </Tag>
        )}
        {task.status === 'VERIFIED' && (
          <Tag icon={<CheckCircleFilled />} color="green">Verified</Tag>
        )}
        {task.status === 'REJECTED' && (
          <Alert
            type="error"
            showIcon
            message={
              <>
                Rejected{task.remarks ? `: ${task.remarks}` : ''}.
                Make corrections and complete again.
              </>
            }
            style={{ fontSize: 12 }}
          />
        )}
      </Space>
    </Card>
  )
}

function CompleteSheet({ task, onClose, onCompleted }: {
  task: TaskInstance | null
  onClose: () => void
  onCompleted: (t: TaskInstance) => void
}) {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [uploadingUrl, setUploadingUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  // Reset internal state every time the sheet opens for a new task.
  useMemo(() => {
    if (task) {
      form.resetFields()
      setFileList([])
      setUploadingUrl(task.evidence_url ?? null)
    }
  }, [task?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const completeMut = useMutation({
    mutationFn: async () => {
      if (!task) throw new Error('No task')
      const vals = await form.validateFields()
      const url = uploadingUrl ?? (vals.evidence_url as string | undefined) ?? undefined
      if (task.evidence_required && !url) {
        throw new Error('Evidence is required for this task')
      }
      return taskApi.complete(task.id, {
        evidence_url: url, remarks: vals.remarks as string,
      })
    },
    onSuccess: (t) => {
      onCompleted(t)
      onClose()
    },
    onError: (e: Error) => message.error(e.message),
  })

  async function uploadFile(file: File) {
    if (!task) return false
    setUploading(true)
    try {
      const r = await taskApi.uploadEvidence(task.id, file)
      setUploadingUrl(r.evidence_url)
      message.success('Evidence uploaded.')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Upload failed')
    } finally {
      setUploading(false)
    }
    return false // prevent AntD's default upload
  }

  return (
    <Drawer
      title={task ? `Complete: ${task.title}` : ''}
      open={!!task}
      onClose={onClose}
      width={420}
      destroyOnClose
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} size="large">Cancel</Button>
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            loading={completeMut.isPending}
            onClick={() => completeMut.mutate()}
          >
            Mark Complete
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        {task?.evidence_required && (
          <Alert
            showIcon
            type="warning"
            message="Evidence is required for this task."
            description="Take a photo, upload a PDF, or paste a link below."
            style={{ marginBottom: 12 }}
          />
        )}
        <Form.Item label="Photo / file evidence">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload
              fileList={fileList}
              onChange={({ fileList: list }) => setFileList(list.slice(-1))}
              beforeUpload={(file) => { uploadFile(file as File); return false }}
              maxCount={1}
              accept="image/*,application/pdf"
              listType="picture"
            >
              <Button icon={<CameraOutlined />} loading={uploading} size="large"
                      style={{ height: 48, width: '100%', fontWeight: 600,
                                borderRadius: 10 }}>
                {uploadingUrl ? 'Replace photo / file' : 'Take photo or attach file'}
              </Button>
            </Upload>
            {uploadingUrl && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <PaperClipOutlined /> Uploaded · {uploadingUrl.split('/').pop()}
              </Text>
            )}
          </Space>
        </Form.Item>
        <Form.Item
          name="evidence_url"
          label="…or paste a link"
          rules={task?.evidence_required && !uploadingUrl
            ? [{ required: true, message: 'Evidence required — upload or paste a link' }]
            : []}
        >
          <Input placeholder="https://…  or  s3://…  (optional if photo uploaded)" />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={3} placeholder="Anything the verifier should know?" />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

function BlockSheet({ task, onClose, onBlocked }: {
  task: TaskInstance | null
  onClose: () => void
  onBlocked: (t: TaskInstance) => void
}) {
  const { message } = App.useApp()
  const [form] = Form.useForm()

  const { data: reasons = [] } = useQuery({
    queryKey: ['exception-reasons'],
    queryFn: () => taskApi.exceptionReasons(),
  })

  useMemo(() => { if (task) form.resetFields() },
    [task?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const blockMut = useMutation({
    mutationFn: async () => {
      if (!task) throw new Error('No task')
      const vals = await form.validateFields()
      return taskApi.block(task.id, {
        reason_code: vals.reason_code as string,
        reason_text: vals.reason_text as string,
      })
    },
    onSuccess: (t) => { onBlocked(t); onClose() },
    onError: (e: Error) => message.error(e.message),
  })

  return (
    <Drawer
      title={task ? `Block: ${task.title}` : ''}
      open={!!task}
      onClose={onClose}
      width={420}
      destroyOnClose
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} size="large">Cancel</Button>
          <Button danger size="large" type="primary"
                  icon={<StopOutlined />}
                  loading={blockMut.isPending}
                  onClick={() => blockMut.mutate()}>
            Mark Blocked
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Alert
          type="info"
          showIcon
          message="Tasks blocked with a 'fair' reason aren't counted against your score."
          style={{ marginBottom: 12 }}
        />
        <Form.Item
          name="reason_code"
          label="Why is this blocked?"
          rules={[{ required: true, message: 'Pick a reason' }]}
        >
          <Select
            size="large"
            options={reasons.map(r => ({
              value: r.reason_code,
              label: `${r.reason_name}${r.penalize_employee_flag ? '' : ' (fair)'}`,
            }))}
            placeholder="Select a reason"
          />
        </Form.Item>
        <Form.Item
          name="reason_text"
          label="Details"
          rules={[{ required: true, min: 5, message: 'Add a brief explanation' }]}
        >
          <Input.TextArea rows={3}
            placeholder="What specifically is blocking this task?" />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
